import FileUpload from "@mui/icons-material/FileUpload";
import {
  Box,
  CircularProgress,
  InputAdornment,
  Link,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { AutocompleteRenderInputParams } from "@mui/material/Autocomplete";
import { snakeCase } from "lodash-es";
import type { ClipboardEventHandler, FC } from "react";
import type { DropzoneInputProps } from "react-dropzone";
import type { UsableAccountPersonField } from "../../../../server/supabase-segment-field-service";
import type { Condition } from "../../types";

interface RenderInputProps {
  params: AutocompleteRenderInputParams;
  isDragActive: boolean;
  openFileUpload: () => void;
  loading: boolean;
  parsing: boolean;
  condition: Condition;
  exportCsvData: string;
  handleEntries: (entries: string[]) => void;
  error?: string;
  disabled?: boolean;
  segmentField?: UsableAccountPersonField;
  getInputProps: <T extends DropzoneInputProps>(props?: T) => T;
}

export const RenderInput: FC<RenderInputProps> = (props) => {
  const {
    params,
    isDragActive,
    openFileUpload,
    loading,
    parsing,
    condition,
    handleEntries,
    exportCsvData,
    error,
    disabled,
    segmentField,
    getInputProps,
  } = props;
  const onPaste: ClipboardEventHandler<HTMLInputElement> = (e) => {
    const pastedData = e.clipboardData.getData("Text");
    const entries = pastedData
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    handleEntries(entries);
    e.preventDefault();
  };

  if (isDragActive) {
    return <DragActiveBox onClick={openFileUpload} />;
  }

  return (
    <Box sx={{ mt: 0.4 }}>
      <TextField
        {...params}
        error={Boolean(error)}
        helperText={
          error || <ExportHelperText condition={condition} exportCsvData={exportCsvData} />
        }
        label={segmentField?.display_name ?? "Value"}
        variant="outlined"
        onPaste={onPaste}
        size="small"
        slotProps={{
          input: {
            ...params.InputProps,
            "data-testid": "segment-value-input",
            endAdornment: (
              <>
                <UploadAdornment
                  loading={loading}
                  parsing={parsing}
                  condition={condition}
                  getInputProps={getInputProps}
                  disabled={disabled}
                  openFileUpload={openFileUpload}
                />
                {params.InputProps.endAdornment}
              </>
            ),
          },
        }}
      />
    </Box>
  );
};

const DragActiveBox: FC<{ onClick: () => void }> = ({ onClick }) => (
  <Box
    sx={{
      mt: 0.4,
      border: "1px dashed",
      borderColor: "divider",
      borderRadius: 1,
      p: 1,
      display: "flex",
      alignItems: "center",
    }}
  >
    <FileUpload
      onClick={onClick}
      sx={{
        cursor: "pointer",
        opacity: 0.5,
        "&:hover": { opacity: 0.7 },
      }}
    />
  </Box>
);

const ExportHelperText: FC<{
  condition: Condition;
  exportCsvData: string;
}> = ({ condition, exportCsvData }) =>
  condition.values.length > 0 ? (
    <Link href={encodeURI(exportCsvData)} download={`${snakeCase(condition.field)}.csv`}>
      <Typography variant="caption">Export CSV of existing values</Typography>
    </Link>
  ) : null;

const UploadAdornment: FC<{
  loading: boolean;
  parsing: boolean;
  condition: Condition;
  getInputProps: RenderInputProps["getInputProps"];
  disabled?: boolean;
  openFileUpload: () => void;
}> = ({ loading, parsing, condition, getInputProps, disabled, openFileUpload }) => {
  if (loading || parsing) return <CircularProgress color="inherit" size={20} />;

  if (condition.values.length > 0) return null;

  return (
    <InputAdornment position="end">
      <input data-testid="file-dropzone" {...getInputProps()} disabled={disabled} />
      <Tooltip
        title={
          <Link
            href="https://help.changeengine.com/Create-Segments-via-CSV-Upload-6ac96245f4ea4dc9b4d19ca54f24472a"
            target="_blank"
            color="inherit"
            underline="always"
            rel="noreferrer"
          >
            You can upload a CSV containing the values you want to use to segment. Click here to
            find out more
          </Link>
        }
      >
        <FileUpload
          onClick={openFileUpload}
          sx={{ cursor: "pointer", "&:hover": { opacity: 0.7 } }}
        />
      </Tooltip>
    </InputAdornment>
  );
};
